import { useState, useEffect, type ReactNode } from "react";
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Box,
  Chip,
  Button,
  Tooltip,
  Divider,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import {
  Groups,
  LocationOn,
  ArrowForward,
  Phone,
  PhoneIphone,
  Email,
  Language,
  Feed,
  Description,
  Sports,
  FaxOutlined,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { allianceCoverageFromDocument } from "../utils/allianceCoverage";
import {
  coverageAllianceCardPctChipSx,
  coverageTierForPercentage,
  coverageTierStatusTooltip,
} from "../utils/coverageGrade";
import { AllianceQuickLinkIcons } from "./AllianceQuickLinkIcons";
import { allianceDetailTo, currentReturnTo } from "../utils/allianceNavigation";
import {
  formatFullAddress,
  googleMapsSearchHref,
  hostnameFromUrl,
  normalizeHttpUrl,
  normalizeContactCompare,
  nonEmpty,
  telHref,
  mailtoHref,
} from "../utils/allianceQuickLinks";

interface AllianceCardProps {
  alliance: any;
}

export function AllianceCard({ alliance }: AllianceCardProps) {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const name = alliance.details?.name || "Nepoznat savez";
  const sport = alliance.details?.sport || "";
  const address = alliance.address;
  const contact = alliance.contact ?? {};
  const membersCount = alliance.members?.length || 0;
  const newsCount = alliance.news?.length || 0;
  const documentsCount = alliance.documents?.length || 0;

  const phoneLandline = nonEmpty(contact.phone);
  const phoneMobile = nonEmpty(contact.mobile);
  const fax = nonEmpty(contact.fax);
  const mail = nonEmpty(contact.mail);
  const web = nonEmpty(alliance.details?.link);
  const placeLine = formatFullAddress(address);

  const samePhones =
    phoneLandline &&
    phoneMobile &&
    normalizeContactCompare(phoneLandline) ===
      normalizeContactCompare(phoneMobile);

  const rawLogo = alliance.details?.logo;
  const logoUrl =
    rawLogo != null && String(rawLogo).trim() !== "" ? String(rawLogo) : null;
  const [logoFailed, setLogoFailed] = useState(false);
  const showLogo = logoUrl != null && !logoFailed;

  useEffect(() => {
    setLogoFailed(false);
  }, [alliance._id, logoUrl]);

  const {
    present: coveragePresent,
    pct: coveragePct,
    total: coverageTotal,
  } = allianceCoverageFromDocument(alliance);

  const summaryItems: { icon: ReactNode; text: string }[] = [];
  if (membersCount > 0) {
    summaryItems.push({
      icon: <Groups />,
      text: `${membersCount} članova`,
    });
  }
  if (newsCount > 0) {
    summaryItems.push({
      icon: <Feed />,
      text: `${newsCount} vijesti`,
    });
  }
  if (documentsCount > 0) {
    summaryItems.push({
      icon: <Description />,
      text: `${documentsCount} dok.`,
    });
  }

  const hasQuickBlock = !!(
    phoneLandline ||
    phoneMobile ||
    fax ||
    mail ||
    web ||
    placeLine
  );

  const iconSx = {
    fontSize: 15,
    display: "block" as const,
  };

  return (
    <Card
      elevation={0}
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
        borderRadius: 2.5,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
        boxShadow:
          theme.palette.mode === "light"
            ? `0 1px 3px ${alpha(theme.palette.common.black, 0.06)}, 0 1px 2px ${alpha(theme.palette.common.black, 0.04)}`
            : `0 2px 8px ${alpha(theme.palette.common.black, 0.35)}`,
        transition: theme.transitions.create(
          ["box-shadow", "transform", "border-color"],
          { duration: 220, easing: theme.transitions.easing.easeOut },
        ),
        "&:hover": {
          transform: "translateY(-3px)",
          borderColor: alpha(theme.palette.secondary.main, 0.42),
          boxShadow:
            theme.palette.mode === "light"
              ? `0 12px 28px ${alpha(theme.palette.secondary.main, 0.14)}, 0 4px 12px ${alpha(theme.palette.common.black, 0.06)}`
              : `0 12px 32px ${alpha(theme.palette.common.black, 0.55)}`,
        },
      }}
    >
      <Box
        sx={{
          height: 3,
          flexShrink: 0,
          background: (t) =>
            `linear-gradient(90deg, ${t.palette.secondary.main} 0%, ${alpha(t.palette.secondary.light, 0.92)} 70%, ${alpha(t.palette.secondary.main, 0.35)})`,
        }}
      />

      <CardContent
        sx={{
          flex: 1,
          p: { xs: 2, sm: 2.25 },
          display: "flex",
          flexDirection: "column",
          gap: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: 2,
            mb: summaryItems.length > 0 || hasQuickBlock ? 1.5 : 0,
            alignItems: "stretch",
          }}
        >
          <Box
            sx={{
              width: { xs: 60, sm: 72 },
              height: { xs: 60, sm: 72 },
              flexShrink: 0,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.secondary.main, 0.06),
              border: "1px solid",
              borderColor: alpha(theme.palette.divider, 0.85),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
              boxSizing: "border-box",
            }}
            aria-hidden
          >
            {showLogo ? (
              <Box
                component="img"
                src={logoUrl}
                alt=""
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  p: 0.75,
                  display: "block",
                }}
                onError={() => setLogoFailed(true)}
              />
            ) : (
              <Sports
                sx={{
                  fontSize: { xs: 30, sm: 34 },
                  color: "secondary.main",
                  opacity: 0.38,
                }}
              />
            )}
          </Box>

          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              gap: 0.75,
            }}
          >
            <Box
              sx={{
                display: "flex",
                gap: 1,
                alignItems: "flex-start",
                justifyContent: "space-between",
              }}
            >
              <Typography
                variant="subtitle1"
                component="h2"
                sx={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: { xs: "1rem", sm: "1.05rem" },
                  lineHeight: 1.35,
                  fontWeight: 700,
                  fontFamily: "'Lexend', sans-serif",
                  letterSpacing: "-0.02em",
                }}
              >
                {name}
              </Typography>
              <Box
                sx={{
                  flexShrink: 0,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-end",
                  gap: 0.5,
                  maxWidth: { xs: "46%", sm: "50%" },
                }}
              >
                {sport ? (
                  <Chip
                    label={sport}
                    size="small"
                    variant="outlined"
                    sx={{
                      height: "auto",
                      py: 0.125,
                      fontSize: "0.625rem",
                      fontWeight: 500,
                      textTransform: "none",
                      letterSpacing: "0.01em",
                      lineHeight: 1.2,
                      borderColor: alpha(theme.palette.secondary.main, 0.35),
                      color: "text.secondary",
                      bgcolor: alpha(theme.palette.secondary.main, 0.045),
                      "& .MuiChip-label": {
                        px: 0.6,
                        py: 0.15,
                        whiteSpace: "normal",
                        display: "block",
                        textAlign: "right",
                      },
                    }}
                  />
                ) : null}
                <Tooltip
                  title={`${coveragePresent} od ${coverageTotal} modula · ${coverageTierStatusTooltip(coverageTierForPercentage(coveragePct))}`}
                  enterTouchDelay={0}
                  placement="top"
                >
                  <Chip
                    label={`${coveragePct}%`}
                    size="small"
                    sx={{
                      height: "auto",
                      py: 0.35,
                      px: 0.2,
                      fontSize: "0.625rem",
                      fontWeight: 700,
                      letterSpacing: "0.03em",
                      border: "none",
                      ...coverageAllianceCardPctChipSx(coveragePct, theme),
                      "& .MuiChip-label": {
                        px: 0.5,
                      },
                    }}
                  />
                </Tooltip>
              </Box>
            </Box>
          </Box>
        </Box>

        {summaryItems.length > 0 && (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: { xs: 1, sm: 1.25 },
              alignItems: "center",
              mb: 1.25,
              rowGap: 0.75,
            }}
          >
            {summaryItems.map((item, i) => (
              <Box
                key={i}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  color: "text.secondary",
                }}
              >
                <Box
                  component="span"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 18,
                    height: 18,
                    flexShrink: 0,
                    "& svg": { fontSize: 16, display: "block" },
                  }}
                >
                  {item.icon}
                </Box>
                <Typography
                  variant="caption"
                  component="span"
                  sx={{
                    fontWeight: 600,
                    letterSpacing: 0.02,
                    fontSize: "0.7rem",
                    lineHeight: 1,
                  }}
                >
                  {item.text}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {hasQuickBlock ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.75,
              mt: summaryItems.length ? 0.5 : 0.25,
            }}
          >
            {samePhones && phoneLandline ? (
              <QuickFact
                icon={<Phone sx={iconSx} />}
                href={telHref(phoneLandline)}
              >
                {phoneLandline}
              </QuickFact>
            ) : (
              <>
                {phoneLandline ? (
                  <QuickFact
                    icon={<Phone sx={iconSx} />}
                    href={telHref(phoneLandline)}
                  >
                    {phoneLandline}
                  </QuickFact>
                ) : null}
                {phoneMobile ? (
                  <QuickFact
                    icon={<PhoneIphone sx={iconSx} />}
                    href={telHref(phoneMobile)}
                  >
                    {phoneMobile}
                  </QuickFact>
                ) : null}
              </>
            )}
            {fax ? (
              <QuickFact icon={<FaxOutlined sx={iconSx} />} href={telHref(fax)}>
                {fax}
              </QuickFact>
            ) : null}
            {mail ? (
              <QuickFact icon={<Email sx={iconSx} />} href={mailtoHref(mail)}>
                {mail}
              </QuickFact>
            ) : null}
            {web ? (
              <QuickFact
                icon={<Language sx={iconSx} />}
                href={normalizeHttpUrl(web)}
              >
                {hostnameFromUrl(web)}
              </QuickFact>
            ) : null}
            {placeLine ? (
              <QuickFact
                icon={<LocationOn sx={iconSx} />}
                href={googleMapsSearchHref(alliance.address)}
              >
                {placeLine}
              </QuickFact>
            ) : null}
          </Box>
        ) : null}
      </CardContent>

      <Divider sx={{ borderColor: alpha(theme.palette.divider, 0.9) }} />

      <CardActions
        sx={{
          px: { xs: 2, sm: 2.25 },
          py: 1.5,
          mt: "auto",
          bgcolor: alpha(theme.palette.text.primary, 0.015),
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.25,
            flexWrap: "wrap",
            flex: 1,
            minWidth: 0,
          }}
        >
          <AllianceQuickLinkIcons alliance={alliance} />
        </Box>
        <Button
          size="small"
          variant="text"
          color="secondary"
          endIcon={<ArrowForward sx={{ fontSize: 18 }} />}
          onClick={() =>
            navigate(allianceDetailTo(alliance._id, currentReturnTo(location)))
          }
          sx={{
            flexShrink: 0,
            minWidth: "max-content",
            fontFamily: "'Lexend', sans-serif",
            fontWeight: 700,
            fontSize: "0.75rem",
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            borderRadius: 1,
            px: 1.25,
            py: 0.5,
            "&:hover": {
              bgcolor: alpha(theme.palette.secondary.main, 0.1),
            },
          }}
        >
          Detalji
        </Button>
      </CardActions>
    </Card>
  );
}

function QuickFact({
  icon,
  children,
  href,
}: {
  icon: ReactNode;
  children: ReactNode;
  href?: string | null;
}) {
  const theme = useTheme();
  if (children == null || String(children).trim() === "") return null;

  const openBlank = href ? /^https?:\/\//i.test(href) : false;
  const textTypographySx = {
    flex: 1,
    minWidth: 0,
    lineHeight: 1.35,
    wordBreak: "break-word" as const,
    color: "text.secondary",
    fontSize: "0.68rem",
    fontWeight: 400,
    letterSpacing: 0.01,
  };

  const textInner =
    href != null && href !== "" ? (
      <Typography
        component="a"
        variant="caption"
        href={href}
        {...(openBlank ? { target: "_blank", rel: "noopener noreferrer" } : {})}
        sx={{
          ...textTypographySx,
          textDecoration: "none",
          "&:hover": { textDecoration: "underline", color: "secondary.main" },
          "&:focus-visible": {
            outline: `2px solid ${theme.palette.secondary.main}`,
            outlineOffset: 2,
            borderRadius: 0.5,
          },
        }}
      >
        {children}
      </Typography>
    ) : (
      <Typography variant="caption" component="span" sx={textTypographySx}>
        {children}
      </Typography>
    );

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 0.75,
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          color: "text.secondary",
          flexShrink: 0,
          width: 22,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          opacity: 0.88,
          pt: "1px",
          "& svg": { display: "block" },
        }}
      >
        {icon}
      </Box>
      {textInner}
    </Box>
  );
}
